import { addDays } from 'date-fns'

import {
  OtoIndividualProductRow,
  OtoIndividualStayPrice,
  ProductOtoIndividual,
  ProductOtoIndividualJourneyPlace
} from '../../../../lib/types/models/product/product-oto-individual'
import { filterDuplicates } from '../../../../lib/utils/functions/filter-duplicates'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'

// allowed number of course hours per week for an OTO Individual proposition
export const OTO_INDIVIDUAL_NUMBER_OF_HOURS = [10, 15, 20, 25]

// an OTO Individual stay lasts from 1 to 8 weeks
export const OTO_INDIVIDUAL_NUMBER_OF_WEEKS = Array.from({ length: 8 }, (_, index) => index + 1)

// what the popup keeps of the selected table row; the frais de séjour come from the backend,
// so they are missing until the row is resolved against the loaded rows
export type OtoIndividualRow = {
  rowId: string
  product: ProductOtoIndividual
  numberOfWeeks: number
  stayPrices?: OtoIndividualStayPrice[]
}

// one row per journey place of an OTO Individual product, for the journey place selection table
export type ProductJourneyPlaceRow = ProductOtoIndividualJourneyPlace & {
  product: Pick<ProductOtoIndividual, 'id' | 'name' | 'country'>
}

// the per-row selection state of the OTO Individual products table
export type OtoIndividualRowDraft = {
  departureDate?: string
  // auto-filled from the departure date and the number of weeks of the row, editable
  returnDate?: string
  numberOfHours?: number
  regionOfStayId?: number
  arrivalPlaceId?: number
}

// must match the row id built by the backend (products-utils.ts)
export const getOtoIndividualRowId = (product: ProductOtoIndividual, numberOfWeeks: number) =>
  `${product.id}-${numberOfWeeks}`

// used by the "Durée en semaines" filter and by the summary of a saved proposition, which have
// no table row to read the label from
export const getOtoIndividualWeeksLabel = (numberOfWeeks: number) =>
  `${numberOfWeeks} semaine${numberOfWeeks > 1 ? 's' : ''} (${numberOfWeeks * 7 - 1} nuits)`

// a product can hold the same lieu de séjour once per option, so the option tells the régions
// apart (e.g. "Région de Malte – Multi-activités")
export const getRegionOfStayLabel = (
  regionOfStay: Pick<ProductOtoIndividualJourneyPlace, 'place' | 'option'>
) => {
  const optionName = getTranslatedName(regionOfStay.option)
  return optionName ? `${regionOfStay.place.name} – ${optionName}` : regionOfStay.place.name
}

// mirrors the backend formula (products-utils.ts) : arrivée le dimanche, retour le samedi,
// une semaine = 6 nuits ; the default of the editable date de retour
export const getOtoIndividualReturnDate = (departureDate: Date, numberOfWeeks: number) =>
  addDays(departureDate, numberOfWeeks * 7 - 1)

// the products of the loaded rows, deduplicated (a product has one row per number of weeks)
export const getOtoIndividualProducts = (rows: OtoIndividualProductRow[]) =>
  filterDuplicates(
    rows.map((row) => row.product),
    (product) => product.id
  )

// the frais de séjour of a row for the selected région du séjour, or the "à partir de" value
export const getOtoIndividualStayPrice = (
  row: Pick<OtoIndividualRow, 'stayPrices'>,
  regionOfStayId?: number
) => row.stayPrices?.find((price) => price.regionOfStayId === regionOfStayId)?.stayPrice
